import { useState, useEffect } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle, X, Eye, Receipt, MagnifyingGlass, Pencil, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Named export — render JUST the verification UI (no <Layout>). Lets us drop
 * the same queue into PaymentPage as a tab (Issue 1, Round 31) so admins
 * see verification + reconciliation + history under one /payments URL.
 */
export function VerificationQueueInner() {
  const [items, setItems] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [programmeFilter, setProgrammeFilter] = useState("all");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState({ amount: "", currency: "NGN", reference: "", note: "", status: "pending_verification", discount_claimed: "", discount_note: "", rejection_reason: "" });

  const load = () => {
    const url = tab === "pending" ? "/payment/transfers/pending" : "/payment/transfers/all";
    api.get(url).then(r => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/public-programmes").then(r => setProgrammes(r.data || [])).catch(() => {}); }, []);

  // Round 33 — confirmation dialog before approving with FX + override.
  const [approveFor, setApproveFor] = useState(null);
  const [approveForm, setApproveForm] = useState({ accepted_amount_usd: "", discount_amount_usd: "", approval_note: "" });
  const [fx, setFx] = useState({ active_usd_to_ngn: 0, active_source: "", stored: {}, live_usd_to_ngn: 0 });

  useEffect(() => {
    api.get("/admin/exchange-rates").then(r => setFx(r.data || {})).catch(() => {});
  }, []);

  const openApprove = (t) => {
    setApproveFor(t);
    // Pre-compute auto-converted USD for the dialog.
    const rate = fx.active_usd_to_ngn || 1;
    const raw = Number(t.amount || 0);
    const isNgn = (t.currency || "").toUpperCase() === "NGN";
    const autoUsd = isNgn && rate > 0 ? Math.round((raw / rate) * 100) / 100 : raw;
    setApproveForm({ accepted_amount_usd: String(autoUsd), discount_amount_usd: "", approval_note: "" });
  };

  const approve = async (id) => {
    // Legacy quick-approve path retained for the (rare) case the dialog was bypassed.
    try {
      await api.post(`/payment/transfers/${id}/approve`);
      toast.success("Payment approved + student notified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmApprove = async () => {
    if (!approveFor) return;
    try {
      const payload = {
        accepted_amount_usd: approveForm.accepted_amount_usd === "" ? null : parseFloat(approveForm.accepted_amount_usd),
        discount_amount_usd: approveForm.discount_amount_usd === "" ? 0 : parseFloat(approveForm.discount_amount_usd),
        approval_note: approveForm.approval_note,
      };
      await api.post(`/payment/transfers/${approveFor.id}/approve`, payload);
      toast.success("Payment approved + student notified");
      setApproveFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reject = async () => {
    if (!rejectFor) return;
    try {
      await api.post(`/payment/transfers/${rejectFor.id}/reject`, { reason: rejectReason });
      toast.success("Payment rejected + student notified");
      setRejectFor(null); setRejectReason(""); load();
    } catch (e) { toast.error("Failed"); }
  };

  const viewReceipt = (id) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/payment/transfers/${id}/receipt`, "_blank");

  const openEdit = (t) => {
    setEditFor(t);
    setEditForm({
      amount: String(t.amount ?? ""),
      currency: t.currency || "NGN",
      reference: t.reference || "",
      note: t.note || "",
      status: t.status || "pending_verification",
      discount_claimed: String(t.discount_claimed ?? ""),
      discount_note: t.discount_note || "",
      rejection_reason: t.rejection_reason || "",
    });
  };

  const saveEdit = async () => {
    if (!editFor) return;
    try {
      await api.put(`/payment/transfers/${editFor.id}`, {
        amount: editForm.amount === "" ? null : parseFloat(editForm.amount),
        currency: editForm.currency,
        reference: editForm.reference,
        note: editForm.note,
        status: editForm.status,
        discount_claimed: editForm.discount_claimed === "" ? null : parseFloat(editForm.discount_claimed),
        discount_note: editForm.discount_note,
        rejection_reason: editForm.rejection_reason,
      });
      toast.success("Transfer updated");
      setEditFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Permanently delete this transfer (${t.user_name} · ${t.currency} ${t.amount})? This cannot be undone.`)) return;
    try {
      await api.delete(`/payment/transfers/${t.id}`);
      toast.success("Transfer deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const filtered = items.filter(t => {
    const s = search.toLowerCase();
    const matchesSearch = !s ||
      (t.user_name || "").toLowerCase().includes(s) ||
      (t.user_email || "").toLowerCase().includes(s) ||
      (t.admission_number || "").toLowerCase().includes(s) ||
      (t.reference || "").toLowerCase().includes(s);
    const matchesProgramme = programmeFilter === "all" || t.programme_id === programmeFilter;
    return matchesSearch && matchesProgramme;
  });

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="admin-payment-verification">
      <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Finance</p>
      <h2 className="text-xl sm:text-2xl font-bold text-slate-950 mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Payment Verification Queue</h2>

      {/* Round 33 — Exchange Rate card */}
      <Card className="mb-6 rounded-none border border-slate-200" data-testid="exchange-rate-card">
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[150px]">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Active USD → NGN</p>
            <p className="text-lg font-bold text-slate-950 font-mono" data-testid="fx-active-rate">{fx.active_usd_to_ngn ? Number(fx.active_usd_to_ngn).toLocaleString() : "—"}</p>
            <p className="text-[10px] text-slate-500">source: <span className="font-mono">{fx.active_source || "—"}</span></p>
          </div>
          <div className="flex-1 min-w-[150px]">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Live (market)</p>
            <p className="text-base text-slate-700 font-mono" data-testid="fx-live-rate">{fx.live_usd_to_ngn ? Number(fx.live_usd_to_ngn).toLocaleString() : "—"}</p>
          </div>
          {fx.stored?.updated_at && (
            <div className="flex-1 min-w-[150px]">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Stored rate</p>
              <p className="text-sm text-slate-700 font-mono">{Number(fx.stored.usd_to_ngn).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500">by {fx.stored.set_by} · {new Date(fx.stored.updated_at).toLocaleDateString()}</p>
            </div>
          )}
          <Button
            size="sm"
            variant="outline"
            className="rounded-none text-xs"
            data-testid="fx-edit-btn"
            onClick={async () => {
              const v = window.prompt("Set USD → NGN rate (number):", String(fx.stored?.usd_to_ngn || fx.active_usd_to_ngn || ""));
              if (!v) return;
              const n = parseFloat(v);
              if (!n || n <= 0) return toast.error("Invalid rate");
              try {
                await api.put("/admin/exchange-rates", { usd_to_ngn: n });
                const r = await api.get("/admin/exchange-rates");
                setFx(r.data || {});
                toast.success("Exchange rate updated");
              } catch (e) {
                toast.error(e.response?.data?.detail || "Failed to update");
              }
            }}
          >Set rate</Button>
        </CardContent>
      </Card>

        <div className="flex gap-2 mb-4 border-b border-slate-200">
          {[
            { k: "pending", l: "Pending Verification" },
            { k: "all", l: "All Transfers" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`pay-tab-${t.k}`} className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.k ? "border-blue-800 text-blue-900" : "border-transparent text-slate-600"}`}>{t.l}</button>
          ))}
        </div>

        <div className="mb-4 flex gap-2 flex-wrap items-end">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, reference…" className="pl-9 pr-3 py-2 border border-slate-200 w-full text-sm rounded-none" data-testid="payment-search" />
          </div>
          <select
            value={programmeFilter}
            onChange={e => setProgrammeFilter(e.target.value)}
            className="border border-slate-200 px-3 py-2 text-sm rounded-none bg-white"
            data-testid="payment-programme-filter"
          >
            <option value="all">All programmes</option>
            {programmes.map(p => (
              <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.title}</option>
            ))}
          </select>
          <span className="text-xs text-slate-500" data-testid="payment-results-count">{filtered.length} of {items.length}</span>
        </div>

        <div className="space-y-2">
          {filtered.length === 0 && <p className="text-sm text-slate-500">No transfers in this tab.</p>}
          {filtered.map(t => (
            <Card key={t.id} className="rounded-none border border-slate-200" data-testid={`transfer-${t.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="font-bold text-sm text-slate-950">{t.user_name}</p>
                      {t.admission_number && (
                        <span className="inline-block bg-blue-50 border border-blue-200 px-2 py-0.5 text-[10px] font-mono font-bold tracking-wider text-blue-900" data-testid={`admission-no-${t.id}`}>
                          {t.admission_number}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider ${
                        t.status === "pending_verification" ? "bg-amber-100 text-amber-800" :
                        t.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                        "bg-red-100 text-red-700"
                      }`}>{t.status.replace("_", " ")}</span>
                    </div>
                    <p className="text-xs text-slate-500">{t.user_email}</p>
                    <p className="text-xs text-slate-500 mt-1">Submitted: {new Date(t.submitted_at).toLocaleString()}</p>
                    {t.reference && <p className="text-xs text-slate-700 mt-1">Reference: <b>{t.reference}</b></p>}
                    {t.note && <p className="text-xs text-slate-600 mt-1 italic">"{t.note}"</p>}
                    {t.discount_claimed > 0 && (
                      <div className="mt-2 inline-block bg-blue-50 border border-blue-200 px-2 py-1 text-[11px] text-blue-900" data-testid={`discount-declared-${t.id}`}>
                        <b>Discount declared:</b> {t.currency === "NGN" ? "₦" : "$"}{Number(t.discount_claimed).toLocaleString()}
                        {t.discount_note && <span className="block italic">"{t.discount_note}"</span>}
                      </div>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-bold text-slate-950">{t.currency === "NGN" ? "₦" : "$"}{Number(t.amount).toLocaleString()}</p>
                    <p className="text-xs text-slate-500">{t.programme_id ? "Programme payment" : t.pod_tier ? `POD · ${t.pod_tier}` : "General"}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 flex-wrap">
                  <Button size="sm" onClick={() => viewReceipt(t.id)} variant="outline" className="rounded-none gap-1 text-xs" data-testid={`view-receipt-${t.id}`}><Eye size={12} /> View Receipt</Button>
                  {t.status === "pending_verification" && (
                    <>
                      <Button size="sm" onClick={() => openApprove(t)} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-none gap-1 text-xs" data-testid={`approve-${t.id}`}><CheckCircle size={12} weight="fill" /> Approve</Button>
                      <Button size="sm" onClick={() => setRejectFor(t)} variant="outline" className="rounded-none gap-1 text-xs text-red-700 border-red-200" data-testid={`reject-${t.id}`}><X size={12} /> Reject</Button>
                    </>
                  )}
                  <Button size="sm" onClick={() => openEdit(t)} variant="outline" className="rounded-none gap-1 text-xs text-blue-800 border-blue-200 hover:bg-blue-50" data-testid={`edit-transfer-${t.id}`}><Pencil size={12} /> Edit</Button>
                  <Button size="sm" onClick={() => remove(t)} variant="outline" className="rounded-none gap-1 text-xs text-red-700 border-red-200 hover:bg-red-50" data-testid={`delete-transfer-${t.id}`}><Trash size={12} /> Delete</Button>
                  {t.status === "rejected" && t.rejection_reason && <span className="text-xs text-red-600 italic">Rejected: {t.rejection_reason}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={!!rejectFor} onOpenChange={v => { if (!v) { setRejectFor(null); setRejectReason(""); } }}>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle>Reject payment from {rejectFor?.user_name}</DialogTitle></DialogHeader>
            <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection (sent to student)" className="rounded-none" rows={4} data-testid="reject-reason-input" />
            <div className="flex gap-2">
              <Button onClick={reject} className="bg-red-600 text-white hover:bg-red-700 rounded-none flex-1" data-testid="confirm-reject">Confirm Reject</Button>
              <Button variant="outline" onClick={() => { setRejectFor(null); setRejectReason(""); }} className="rounded-none flex-1">Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Round 29 — edit-transfer dialog */}
        <Dialog open={!!editFor} onOpenChange={v => { if (!v) setEditFor(null); }}>
          <DialogContent className="rounded-none sm:max-w-md" data-testid="edit-transfer-dialog">
            <DialogHeader><DialogTitle>Edit transfer · {editFor?.user_name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Amount</Label>
                  <Input type="number" value={editForm.amount} onChange={e => setEditForm({...editForm, amount: e.target.value})} className="rounded-none mt-1" data-testid="edit-amount" />
                </div>
                <div>
                  <Label className="text-xs">Currency</Label>
                  <Select value={editForm.currency} onValueChange={v => setEditForm({...editForm, currency: v})}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="edit-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NGN">NGN</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input value={editForm.reference} onChange={e => setEditForm({...editForm, reference: e.target.value})} className="rounded-none mt-1" data-testid="edit-reference" />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Textarea rows={2} value={editForm.note} onChange={e => setEditForm({...editForm, note: e.target.value})} className="rounded-none mt-1" data-testid="edit-note" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm({...editForm, status: v})}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_verification">Pending Verification</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">Changing to <b>Approved</b> activates the enrolment + sets payment_status=paid. <b>Rejected</b> reverts to pending_payment.</p>
              </div>
              {editForm.status === "rejected" && (
                <div>
                  <Label className="text-xs">Rejection reason</Label>
                  <Textarea rows={2} value={editForm.rejection_reason} onChange={e => setEditForm({...editForm, rejection_reason: e.target.value})} className="rounded-none mt-1" data-testid="edit-rejection-reason" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button onClick={saveEdit} className="flex-1 bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="edit-save-btn">Save changes</Button>
                <Button variant="outline" onClick={() => setEditFor(null)} className="flex-1 rounded-none">Cancel</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Round 33 — Approve dialog with FX conversion + admin override */}
        <Dialog open={!!approveFor} onOpenChange={v => { if (!v) setApproveFor(null); }}>
          <DialogContent className="rounded-none sm:max-w-md" data-testid="approve-dialog">
            <DialogHeader><DialogTitle>Confirm approval · {approveFor?.user_name}</DialogTitle></DialogHeader>
            {approveFor && (
              <div className="space-y-3">
                <div className="bg-slate-50 border-l-4 border-amber-500 p-3">
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Raw receipt</p>
                  <p className="text-lg font-bold text-slate-950" data-testid="approve-raw-amount">
                    {(approveFor.currency || "").toUpperCase() === "NGN" ? "₦" : "$"}
                    {Number(approveFor.amount).toLocaleString()} {approveFor.currency?.toUpperCase()}
                  </p>
                  {(approveFor.currency || "").toUpperCase() === "NGN" && fx.active_usd_to_ngn > 0 && (
                    <p className="text-xs text-slate-600 mt-1" data-testid="approve-fx-line">
                      Auto-converted: <b>${(Number(approveFor.amount) / fx.active_usd_to_ngn).toFixed(2)}</b> at <b>{Number(fx.active_usd_to_ngn).toLocaleString()}</b>/USD ({fx.active_source})
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Accepted Amount (USD)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={approveForm.accepted_amount_usd}
                    onChange={e => setApproveForm({ ...approveForm, accepted_amount_usd: e.target.value })}
                    className="rounded-none mt-1 font-mono"
                    data-testid="approve-accepted-usd"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Override the auto-converted figure if the deposit hit your bank in a different amount.</p>
                </div>
                <div>
                  <Label className="text-xs">Discount (USD) <span className="text-slate-400">— optional</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={approveForm.discount_amount_usd}
                    onChange={e => setApproveForm({ ...approveForm, discount_amount_usd: e.target.value })}
                    className="rounded-none mt-1 font-mono"
                    data-testid="approve-discount-usd"
                  />
                </div>
                <div>
                  <Label className="text-xs">Note <span className="text-slate-400">— optional</span></Label>
                  <Textarea
                    rows={2}
                    value={approveForm.approval_note}
                    onChange={e => setApproveForm({ ...approveForm, approval_note: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid="approve-note"
                  />
                </div>
                <div className="bg-emerald-50 border-l-4 border-emerald-500 p-3 text-xs" data-testid="approve-balance-preview">
                  <p className="uppercase tracking-widest text-emerald-700 font-bold text-[10px]">Resulting balance preview</p>
                  <p className="text-emerald-900 mt-1">
                    +{(Number(approveForm.accepted_amount_usd || 0) + Number(approveForm.discount_amount_usd || 0)).toFixed(2)} USD will be credited to the enrolment ledger.
                  </p>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={confirmApprove} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-none" data-testid="approve-confirm-btn">Confirm Approval</Button>
                  <Button variant="outline" onClick={() => setApproveFor(null)} className="flex-1 rounded-none">Cancel</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
    </div>
  );
}

// Legacy standalone page — preserved so any direct deep-link still works.
// Issue 1 (Round 31): the primary entry point is now /payments → "Verification Queue" tab.
export default function AdminPaymentVerificationPage() {
  return (
    <Layout>
      <VerificationQueueInner />
    </Layout>
  );
}
